import React from "react";
import { useFormContext } from "react-hook-form";
import useSetFormData from "repoV2/features/Common/modules/FormData/utils/useSetFormData";
import {
    formatTimezoneOffset,
    setUserTimezone,
} from "repoV2/features/Common/modules/Timezone/Timezone.utils";
import { TIMEZONE_DATA_LIST } from "repoV2/features/Common/modules/Timezone/Timezone.constants";
import useGetFormData from "repoV2/features/Common/modules/FormData/utils/useGetFormData";
import { ITimezoneDetails } from "repoV2/features/Common/modules/FormData/interfaces/TimezoneSelector.interfaces";
import { useCheckoutBasicDetailsContext } from "repoV2/features/CheckoutPages/modules/CheckoutBasicDetailsContextProvider";
import { FORM_KEYS } from "repoV2/features/Common/modules/FormData/FormData.constants";
import { IFormattedTimezoneOptions } from "./TimezoneSelector.interfaces";
import { checkout_form } from "../../../../../CheckoutForm/CheckoutForm.constants";
import { useHideSessionDetailsFormGroup } from "../../SessionDetailsFormGroup.utils";

const {
    timezone_details: { field_names },
} = checkout_form;

export const useTimezoneSelector = () => {
    const { getValues } = useFormContext();
    const { setFormData } = useSetFormData();

    const { formatted_timezones_map } = getFormattedTimezoneOptions();
    const fieldName = field_names.timezone_data;
    const setTimezoneData = ({ value }: { value?: string }) => {
        const data = getValues();
        const timezoneData = formatted_timezones_map[value || data[fieldName]];

        // Note that there are two timezone fields, and one redux for them,
        // so we only change the redux in onChange, form ref values are set in
        // useSyncTimezone in repoV2/features/CheckoutPages/modules/PaymentDetailsSection/modules/FormGroups/modules/SessionDetailsFormGroup/modules/TimezoneSelector/TimezoneSelector.utils.ts
        setFormData(FORM_KEYS.timezone_details, timezoneData.data);
        setUserTimezone({ selectedUserTimezone: timezoneData.data.name });
    };

    const timezoneDetails = useGetFormData<ITimezoneDetails>({
        key: `${FORM_KEYS.timezone_details}`,
    });

    return {
        fieldName,
        setTimezoneData,
        timezoneDetails,
    };
};

export const useSyncTimezone = () => {
    const { watch, setValue } = useFormContext();

    const { listingType, listingStatus } = useCheckoutBasicDetailsContext();

    const { hideSessionDetailsFormGroup } = useHideSessionDetailsFormGroup({
        listingType,
        listingStatus,
    });

    const timeZoneValueOne = watch(field_names.timezone_data);
    const timeZoneValueTwo = watch(
        field_names.timezone_data_description_section
    );

    /**
     * We have two timezone fields, one in payment form,
     * other outside of it. We need to keep them both synced.
     *
     * Reason to keep two fields?
     * 'react-hook-form' does not allow us to keep two fields with
     * same name. There must be a unique identifier for each field.
     *
     * So, we keep two timezones, and keep them in sync.
     */
    React.useEffect(() => {
        // Checking 'hideSessionDetailsFormGroup' here as if this is true, then
        // we are not showing the timezone card, so no need to setValue
        if (
            !hideSessionDetailsFormGroup &&
            timeZoneValueOne !== timeZoneValueTwo
        ) {
            // Not setting redux here as it is being handled through the onChange of the timezone selector
            setValue(
                field_names.timezone_data_description_section,
                timeZoneValueOne
            );
        }
    }, [timeZoneValueOne, setValue]);

    React.useEffect(() => {
        // Checking 'hideSessionDetailsFormGroup' here as if this is true, then
        // we are not showing the timezone card, so no need to setValue
        if (
            !hideSessionDetailsFormGroup &&
            timeZoneValueOne !== timeZoneValueTwo
        ) {
            // Not setting redux here as it is being handled through the onChange of the timezone selector
            setValue(field_names.timezone_data, timeZoneValueTwo);
        }
    }, [timeZoneValueTwo, setValue]);
};

export const getFormattedTimezoneOptions = () => {
    const formatted_timezone_options: Array<IFormattedTimezoneOptions> = [];
    const formatted_timezones_map: {
        [key: string]: IFormattedTimezoneOptions;
    } = {};
    TIMEZONE_DATA_LIST.forEach(({ country_code, offset, name }) => {
        const processedOffset = formatTimezoneOffset(offset);
        const formattedData = {
            id: `${country_code}-${name}`,
            value: name,
            label: `GMT ${processedOffset} | ${name}`,
            selectedLabel: `GMT ${processedOffset}`,
            data: {
                country_code,
                offset,
                name,
            },
        };
        formatted_timezone_options.push(formattedData);
        formatted_timezones_map[name] = formattedData;
    });
    return {
        formatted_timezone_options,
        formatted_timezones_map,
    };
};
